import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { GovKind, downloadExport, governanceErrorMessage } from "../../../../utils/icGovernance";
import { PRIMARY, statusLabel, statusesFor } from "../governanceStatus";
import { DEFAULT_SORT, MAX_Q, SORT_FIELDS } from "./useRegisterItems";
import { MUTED } from "./cells";

/**
 * The register filter row: status multi-select, debounced search, sort, the
 * Columns menu and the spreadsheet export. Module-scope components only — the
 * register re-renders on rotation, and inner component types would remount.
 */

const OUTLINE_BUTTON_SX = {
  textTransform: "none",
  fontWeight: 600,
  color: "#2c2c34",
  borderColor: "#c3c9d3",
  backgroundColor: "white",
  "&:hover": { borderColor: "#8a8a93", backgroundColor: "#fafafb" },
} as const;

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

type StatusFilterProps = {
  kind: GovKind;
  value: string[];
  onChange: (next: string[]) => void;
  /** Phones get a compact multi-select instead of a row of chips. */
  compact: boolean;
  /** The statuses to offer (default: the kind's own; the Tracker offers tasks' and issues'). */
  statuses?: string[];
};

export const StatusFilter = ({ kind, value, onChange, compact, statuses: offered }: StatusFilterProps) => {
  const statuses = offered ?? statusesFor(kind);
  const selected = new Set(value);
  const toggle = (status: string) => {
    const next = selected.has(status) ? value.filter((s) => s !== status) : [...value, status];
    // Keep the kind's own order so the URL stays stable.
    onChange(statuses.filter((s) => next.includes(s)));
  };

  if (compact) {
    return (
      <Select
        multiple
        displayEmpty
        size="small"
        value={value}
        onChange={(e) => {
          const v = e.target.value;
          const list = typeof v === "string" ? v.split(",") : v;
          onChange(statuses.filter((s) => list.includes(s)));
        }}
        input={<OutlinedInput />}
        renderValue={(v) =>
          v.length === 0 ? "All statuses" : v.length === 1 ? statusLabel(v[0]) : `${v.length} statuses`
        }
        inputProps={{ "aria-label": "Filter by status" }}
        sx={{ minWidth: 180, flex: "1 1 180px", backgroundColor: "white" }}
      >
        {statuses.map((s) => (
          <MenuItem key={s} value={s}>
            <Checkbox size="small" checked={selected.has(s)} sx={{ py: 0 }} />
            <ListItemText primary={statusLabel(s)} />
          </MenuItem>
        ))}
      </Select>
    );
  }

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      useFlexGap
      sx={{ flexWrap: "wrap" }}
      role="group"
      aria-label="Filter by status"
    >
      <Typography variant="body2" sx={{ color: MUTED }}>
        Status:
      </Typography>
      {statuses.map((s) => {
        const on = selected.has(s);
        return (
          <Chip
            key={s}
            label={statusLabel(s)}
            size="small"
            clickable
            aria-pressed={on}
            onClick={() => toggle(s)}
            variant={on ? "filled" : "outlined"}
            sx={{
              fontWeight: on ? 700 : 500,
              borderColor: on ? PRIMARY : "#c3c9d3",
              backgroundColor: on ? "#f8e9f1" : "white",
              color: on ? PRIMARY : "#3f4753",
              border: "1px solid",
            }}
          />
        );
      })}
      {value.length > 0 ? (
        <Button size="small" onClick={() => onChange([])} sx={{ textTransform: "none", color: MUTED }}>
          Clear
        </Button>
      ) : null}
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Search (debounced into the URL)
// ---------------------------------------------------------------------------

export const SearchBox = ({ value, onChange }: { value: string; onChange: (q: string) => void }) => {
  const [text, setText] = useState(value);
  const lastSent = useRef(value);

  // Follow outside changes (back / forward, a cleared URL).
  useEffect(() => {
    if (value !== lastSent.current) {
      lastSent.current = value;
      setText(value);
    }
  }, [value]);

  useEffect(() => {
    const trimmed = text.trim();
    if (trimmed === lastSent.current) return;
    const timer = window.setTimeout(() => {
      lastSent.current = trimmed;
      onChange(trimmed);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [text, onChange]);

  return (
    <TextField
      size="small"
      type="search"
      placeholder="Search title or ID"
      value={text}
      onChange={(e) => setText(e.target.value)}
      inputProps={{ maxLength: MAX_Q, "aria-label": "Search title or ID" }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" sx={{ color: MUTED }} />
          </InputAdornment>
        ),
      }}
      sx={{ width: { xs: "100%", sm: 260 }, backgroundColor: "white" }}
    />
  );
};

// ---------------------------------------------------------------------------
// Sort
// ---------------------------------------------------------------------------

const SORT_OPTIONS = SORT_FIELDS.flatMap((f) => [
  { value: f.field, label: `${f.label} ↑` },
  { value: `-${f.field}`, label: `${f.label} ↓` },
]);

export const SortSelect = ({ value, onChange }: { value: string; onChange: (sort: string) => void }) => (
  <TextField
    select
    size="small"
    label="Sort"
    value={value || DEFAULT_SORT}
    onChange={(e) => onChange(e.target.value)}
    sx={{ minWidth: 160, backgroundColor: "white" }}
  >
    {SORT_OPTIONS.map((o) => (
      <MenuItem key={o.value} value={o.value}>
        {o.label}
      </MenuItem>
    ))}
  </TextField>
);

// ---------------------------------------------------------------------------
// Columns menu
// ---------------------------------------------------------------------------

type ColumnsMenuProps = {
  columns: { key: string; header: string }[];
  hidden: Set<string>;
  onToggle: (key: string) => void;
  onReset: () => void;
};

export const ColumnsMenu = ({ columns, hidden, onToggle, onReset }: ColumnsMenuProps) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  // The first column (the ID) always stays.
  const toggleable = columns.slice(1);
  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<ViewColumnOutlinedIcon />}
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="true"
        aria-expanded={anchor ? "true" : undefined}
        sx={OUTLINE_BUTTON_SX}
      >
        Columns{hidden.size > 0 ? ` (${hidden.size} hidden)` : ""}
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{ sx: { maxHeight: 420 } }}
      >
        {toggleable.map((c) => (
          <MenuItem key={c.key} dense onClick={() => onToggle(c.key)}>
            <Checkbox size="small" checked={!hidden.has(c.key)} sx={{ py: 0 }} />
            <ListItemText primary={c.header} />
          </MenuItem>
        ))}
        <Box sx={{ px: 2, pt: 1 }}>
          <Button size="small" onClick={onReset} sx={{ textTransform: "none", color: PRIMARY }}>
            Reset to default
          </Button>
        </Box>
      </Menu>
    </>
  );
};

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

/** The export answers a blob even on errors: read the JSON message out of it. */
const exportErrorMessage = async (err: any): Promise<string> => {
  const data = err?.response?.data;
  if (typeof Blob !== "undefined" && data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return governanceErrorMessage({ response: { status: err.response.status, data: parsed } });
    } catch {
      // fall through
    }
  }
  return governanceErrorMessage(err);
};

export const ExportButton = ({
  kind,
  onError,
  label = "Export .xlsx",
}: {
  kind: GovKind;
  onError: (message: string | null) => void;
  /** Button text (a register with two exports names each one). */
  label?: string;
}) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (busy) return;
    setBusy(true);
    onError(null);
    try {
      await downloadExport(kind, "xlsx");
    } catch (err) {
      console.error("Governance export failed:", err);
      onError(await exportErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      variant="outlined"
      size="small"
      onClick={run}
      disabled={busy}
      startIcon={busy ? <CircularProgress size={14} /> : <FileDownloadOutlinedIcon />}
      sx={OUTLINE_BUTTON_SX}
    >
      {busy ? "Exporting…" : label}
    </Button>
  );
};
